import SystemUpdateAltOutlinedIcon from "@mui/icons-material/SystemUpdateAltOutlined";
import {
    Checkbox,
    Dialog,
    FormControlLabel,
    Grid,
    MenuItem,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import moment from "moment";
import React, {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from "react";
import { CSVLink } from "react-csv";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import DialogBody from "../../../components/Dialog/Body";
import DialogActionButtons from "../../../components/Dialog/Footer";
import DialogHeader from "../../../components/Dialog/Header";
import CustomDropDown from "../../../components/CutomDropDown";
import Label from "../../../components/Label";
import { CURRENCY } from "../../../constants/Variables";
import globalStyle from "../../../globalStyle";
import { getCharityOrganizationList } from "../../../services/charityOrganizationApiFn";
import { exportDonations } from "../../../services/donationApiFn";
import { handleOpenSnackBar } from "../../../utils";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
    "& .MuiDialogContent-root": {
        padding: theme.spacing(2),
    },
    "& .MuiDialogActions-root": {
        padding: theme.spacing(2),
    },
    "& .MuiPaper-root": {
        width: "500px",
        [theme.breakpoints.down("sm")]: {
            margin: 12,
        },
    },
}));

const initialInputValues = {
    charityOrganization: "",
    startDate: null,
    endDate: null,
};

export const DonationExportDialog = forwardRef(({ }, ref) => {
    const { t } = useTranslation(["donation", "common"]);
    const csvLinkRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [isLoading, setIsLoading] = useState({
        export: false,
    });
    const [inputValues, setInputValues] = useState(initialInputValues);
    const [inputError, setInputError] = useState({});
    const [exportWithoutFilter, setExportWithoutFilter] = useState(false);
    const [charityOrganizationList, setCharityOrganizationList] = useState([]);
    const [exportData, setExportData] = useState([]);
    const [shouldDownloadCsv, setShouldDownloadCsv] = useState(false);
    const { currentLanguage } = useSelector((state) => state.languageReducer);

    const handleGetCharityOrganizationList = async () => {
        try {
            const response = await getCharityOrganizationList();
            if (response.data.status === "success") {
                setCharityOrganizationList(response.data.data || []);
            } else {
                handleOpenSnackBar("error", response.data.message);
            }
        } catch (error) {
            handleOpenSnackBar("error", error.message);
        }
    };

    const resetForm = () => {
        setInputValues(initialInputValues);
        setInputError({});
        setExportWithoutFilter(false);
        setExportData([]);
        setShouldDownloadCsv(false);
        setCharityOrganizationList([]);
    };

    useImperativeHandle(ref, () => ({
        openDialog() {
            resetForm();
            setOpen(true);
            handleGetCharityOrganizationList();
        },

        closeDialog() {
            handleClose();
        },
    }));

    const handleClose = () => {
        setOpen(false);
        resetForm();
    };

    const handleChageInputs = (e) => {
        const { name, value } = e.target;
        setInputValues((prev) => ({
            ...prev,
            [name]: value,
        }));
        setInputError((prev) => ({
            ...prev,
            [name]: "",
        }));
    };

    const handleDateChange = (date, name) => {
        setInputValues((pre) => ({ ...pre, [name]: date }));
        setInputError((pre) => ({ ...pre, [name]: "" }));
    };

    const handleExportWithoutFilterChange = (event) => {
        const checked = event.target.checked;
        setExportWithoutFilter(checked);
        if (checked) {
            setInputError({});
        }
    };

    const validateExportForm = () => {
        if (exportWithoutFilter) {
            return false;
        }

        const errors = {};
        if (!inputValues.charityOrganization) {
            errors.charityOrganization = "validation.charityOrganizationIsRequired";
        }
        if (!inputValues.startDate) {
            errors.startDate = "validation.startDateIsRequired";
        }
        if (!inputValues.endDate) {
            errors.endDate = "validation.endDateIsRequired";
        }

        setInputError(errors);
        return Object.keys(errors).length > 0;
    };

    const formatDateParam = (date) => {
        if (!date) {
            return null;
        }
        return dayjs(date).format("YYYY-MM-DD");
    };

    const prepareExportData = async () => {
        const params = exportWithoutFilter
            ? {}
            : {
                charityOrganization: inputValues.charityOrganization,
                startDate: formatDateParam(inputValues.startDate),
                endDate: formatDateParam(inputValues.endDate),
            };

        const response = await exportDonations(params);

        if (response.data.status !== "success") {
            handleOpenSnackBar("error", response.data.message);
            return [];
        }

        const data = response?.data?.data || [];

        return data.map((item) => ({
            "Created At": item?.createdAt
                ? moment(item.createdAt).format("MM-DD-YYYY")
                : "-",
            "Order Id": item?.order?.orderId || "-",
            "Organization Name": item?.organization?.name?.[currentLanguage] || "-",
            "User Name": `${item?.user?.firstname || ""} ${item?.user?.lastname || ""}`.trim() || "-",
            "User Email": item?.user?.email || "-",
            "Order Sub Total": `${item?.orderSubtotal ?? 0} ${CURRENCY}`,
            "Donation Percentage": `${item?.donationPercentage ?? 0} %`,
            "Donation Expected Amount": `${item?.donationAmount ?? 0} ${CURRENCY}`,
            "Donation Actual Amount": `${item?.actualAmount ?? 0} ${CURRENCY}`,
        }));
    };

    useEffect(() => {
        if (shouldDownloadCsv && exportData.length > 0) {
            csvLinkRef.current?.link?.click();
            setShouldDownloadCsv(false);
            handleOpenSnackBar("success", t("exportSuccess"));
        }
    }, [shouldDownloadCsv, exportData, t]);

    const handleExportDonations = async () => {
        if (validateExportForm()) {
            return;
        }

        try {
            setIsLoading({ export: true });
            const data = await prepareExportData();

            if (data.length === 0) {
                handleOpenSnackBar("error", t("noDonationsToExport"));
                return;
            }

            setExportData(data);
            setShouldDownloadCsv(true);
        } catch (error) {
            handleOpenSnackBar("error", error.message);
        } finally {
            setIsLoading({ export: false });
        }
    };

    return (
        <BootstrapDialog
            onClose={handleClose}
            aria-labelledby="customized-dialog-title"
            open={open}
        >
            <DialogHeader
                title={t("exportDonations")}
                secondaryHeading={t("exportDonationsInstruction")}
                icon={<SystemUpdateAltOutlinedIcon />}
                onClose={handleClose}
            />

            <DialogBody>
                <Grid container spacing={2}>
                    {!exportWithoutFilter && (
                        <>
                            <Grid item xs={12}>
                                <Label
                                    required
                                    labelText={t("charityOrganization")}
                                    htmlFor={"charityOrganization"}
                                />
                                <CustomDropDown
                                    id={"charityOrganization"}
                                    name={"charityOrganization"}
                                    value={inputValues.charityOrganization}
                                    onChange={handleChageInputs}
                                    inputError={inputError.charityOrganization}
                                    errorText={t(inputError.charityOrganization)}
                                >
                                    <MenuItem disabled>{t("selectCharityOrganization")}</MenuItem>
                                    {charityOrganizationList?.map((val) => (
                                        <MenuItem
                                            key={val._id}
                                            value={val._id}
                                            sx={{ textTransform: "capitalize" }}
                                        >
                                            {val.name[currentLanguage]}
                                        </MenuItem>
                                    ))}
                                </CustomDropDown>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Label
                                    required
                                    labelText={t("startDate")}
                                    htmlFor={"start-date"}
                                />
                                <DatePicker
                                    id={"start-date"}
                                    sx={{ width: "100%", ...globalStyle.textField }}
                                    name={"startDate"}
                                    value={inputValues.startDate}
                                    onChange={(date) => handleDateChange(date, "startDate")}
                                    slotProps={{
                                        textField: {
                                            error: Boolean(inputError.startDate),
                                            helperText: t(inputError.startDate),
                                        },
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Label
                                    required
                                    labelText={t("endDate")}
                                    htmlFor={"end-date"}
                                />
                                <DatePicker
                                    id={"end-date"}
                                    sx={{ width: "100%", ...globalStyle.textField }}
                                    minDate={inputValues.startDate}
                                    name={"endDate"}
                                    value={inputValues.endDate}
                                    onChange={(date) => handleDateChange(date, "endDate")}
                                    slotProps={{
                                        textField: {
                                            error: Boolean(inputError.endDate),
                                            helperText: t(inputError.endDate),
                                        },
                                    }}
                                />
                            </Grid>
                        </>
                    )}

                    <Grid item xs={12}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={exportWithoutFilter}
                                    onChange={handleExportWithoutFilterChange}
                                />
                            }
                            label={t("exportWithoutFilter")}
                        />
                    </Grid>
                </Grid>
            </DialogBody>

            <DialogActionButtons
                onConfirm={handleExportDonations}
                onCancel={handleClose}
                confirmText={"export"}
                cancelText={"cancel"}
                isConfirmBtnDisable={isLoading.export}
                showCancelButton={true}
            />

            <CSVLink
                ref={csvLinkRef}
                data={exportData}
                filename={"donations.csv"}
                className="csv-link"
                style={{ display: "none" }}
            />
        </BootstrapDialog>
    );
});
